import { TranslationKeys, PostProcessKeys } from 'translations';
import {
  Field,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  InfiniteData,
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  UseQueryOptions,
} from 'react-query';
import { DataFileEntity } from './DataFiles/models';
import { DropzoneFile } from 'dropzone';
import { FEATURE_FLAGS } from 'utils/constants';

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface GenericObject {
  [key: string]: any | any[];
}

export interface Subscription {
  id: number;
  users: number[];
  owner: number;
  submgt_customer_id: string;
  submgt_subscription_id: string;
  active: true;
  created_at: string;
  updated_at: string;
}

export interface Avatar {
  alternativeText: string;
  caption: string;
  created_at: string;
  ext: string;
  hash: string;
  height: number;
  id: number;
  mime: string;
  name: string;
  previewUrl: string;
  provider: string;
  provider_metadata: { public_id: string; resource_type: string };
  size: number;
  updated_at: string;
  url: string;
  width: number;
}
export interface ExternalLink {
  id: number;
  site_name: string;
  url: string;
}

export interface DataProviderEntity {
  id: number;
  name: string;
  fka: string;
  owned_by: string;
  description: string;
  contributor_slug: string;
  classification: string;
  vague_description: string;
  short_description: string;
  long_description: string;
  created_at: string;
  updated_at: string;
  hide_identity_allowed: false;
  contact: {
    id: number;
    name: string;
    email: string;
  };
  avatar: { id: number; image: CloudinaryResponse };
  external_links: ExternalLink[];
  data_details: null;
  multiple_active_users: boolean;
  avatar_url?: string;
}

export interface NotificationSettings {
  id: number;
  email_org: boolean;
  email_user: boolean;
  email_new_for_review_search: boolean;
  email_new_provider_response_for_reviewed_requests: boolean;
  email_new_invoice: boolean;
}

export interface User {
  active: boolean;
  admin: boolean;
  avatar: CloudinaryResponse | null;
  created_at: string;
  data_files: [];
  data_provider: DataProviderEntity;
  email: string;
  first_name: string;
  id: number;
  job_title: string;
  last_login: null;
  last_name: string;
  new_user: false;
  notification_settings: NotificationSettings | null;
  organization: Organization;
  subscription: Subscription | null;
  talkjs_id: string;
  talkjs_signature: string;
  updated_at: string;
  username: string;
  waitlisted: boolean;
  nomad_admin: boolean;
  ghosting_admin: boolean;
  single_person_organization: boolean;
  master_organization_name: string;
  plans: string[];
  auth0_email_verified: boolean;
  avatar_url: string | null;
}

export interface FeatureFlags {
  fallback_content: string;
  id: number;
  name: string;
  value: boolean;
}

export type FeatureFlagKeyValue = Partial<Record<FEATURE_FLAGS, boolean>>;

export interface StrapiApiError {
  message: string;
  response: {
    data: {
      message: { messages: { message: string }[] }[];
    };
  };
}

export interface CloudinaryResponse {
  id: number;
  name: string;
  alternativeText: string;
  caption: string;
  width: number;
  height: number;
  formats: {
    thumbnail: {
      ext: string;
      url: string;
      hash: string;
      mime: string;
      path: string | null;
      size: number;
      width: number;
      height: number;
      provider_metadata: {
        public_id: string;
        resource_type: string;
      };
    };
  } | null;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: null;
  provider: string;
  provider_metadata: {
    public_id: string;
    resource_type: string;
  };
  created_at: string;
  updated_at: string;
  related?: Array<unknown>;
}

export interface MultiDropdownItem {
  name: string;
  id: number;
  isDisabled?: boolean;
  tooltip?: string;
}

export interface ItemList {
  is_all: boolean;
  list: MultiDropdownItem[];
  view_all?: boolean;
}

export interface SearchFilterType {
  id: number;
  filter: {
    id?: number;
    name: string;
    description?: string | null;
    type?: 'category' | 'boolean' | 'numeric';
    key?: string;
    created_at?: string;
    updated_at?: string;
  };
  search?: number;
  data: {
    filter_value: string | string[] | boolean | ItemList;
    filter_units?: string;
  };
}

export interface SearchQuestionEntity {
  id: number;
  name: string;
}

export interface NoteEntity {
  id: number;
  title: string;
  content: string;
  severity: 'info' | 'warning';
  icon: string;
}

export type ReviewStatusType =
  | 'approved'
  | 'under_review'
  | 'needs_editting'
  | 'denied';

export interface SearchEntity {
  active: boolean;
  extended: boolean;
  expires_at: string;
  created_at: string;
  description: string;
  files: unknown[];
  filters: Array<SearchFilterType>;
  hide_identity: boolean;
  id: number;
  name: string;
  questions: SearchQuestionEntity[];
  updated_at: string;
  last_update: string;
  last_internal_update: string;
  last_external_update: string;
  submitted_by?: User;
  organization?: {
    classification: string;
    name: string;
  };
  note?: NoteEntity;
  editable?: boolean;
  review_status: ReviewStatusType;
  reviewer_response: boolean;
  assigned_to: ReadOnlyUser | null;
  free: boolean;
  last_check_description?: CheckRequestDescriptionContent;
}

export interface ProposalAnswerEntity {
  id: number;
  question: {
    id: number;
    name: string;
    created_at?: string;
    updated_at?: string;
  };
  response: string;
}

export interface ProposalEntitySubmittedBy {
  id?: number;
  first_name: string;
  last_name: string;
  avatar?: Partial<CloudinaryResponse>;
  organization_name?: string;
  username?: string;
  email?: string;
  job_title?: string;
}

export enum EActivityType {
  email = 'email',
  phone_call = 'phone_call',
  note = 'note',
  meeting = 'meeting',
  data_evaluation = 'data_evaluation',
  responded_to_request = 'responded_to_request',
  request_created = 'request_created',
  request_archived = 'request_archived',
  request_resubmitted = 'request_resubmitted',
  request_edited = 'request_edited',
  request_approved = 'request_approved',
  request_responded_to = 'request_responded_to',
  file_created = 'file_created',
  file_deleted = 'file_deleted',
  proposal_rejected = 'proposal_rejected',
  proposal_accepted = 'proposal_accepted',
  data_vendor_created = 'data_vendor_created',
  contact_created = 'contact_created',
  link_created = 'link_created',
}

export interface Activity extends ACLEntity {
  id: number;
  type: keyof typeof EActivityType;
  timestamp: string;
  description: string;
  organization: number;
  creator: number;
  system_generated: boolean;
  relationships: ActivityAssociation[];
  title: string | null;
}

export enum FilePreprocessStatus {
  INCOMPLETE = 'incomplete',
  RUNNING = 'running',
  COMPLETE = 'complete',
  ERROR = 'error',
}

export enum FileType {
  legal_document = 'legal_document',
  data_dictionary = 'data_dictionary',
  marketing_materials = 'marketing_materials',
  sample_data = 'sample_data',
  technical_documentation = 'technical_documentation',
  other = 'other',
}

export interface File {
  id: number;
  name: string;
  description: string;
  file: string;
  type: FileType;
  timestamp: string;
  upload_time: string;
  owner: Pick<User, 'id' | 'first_name' | 'last_name'>;
  relationships: ActivityAssociation[];
  preprocess_status: FilePreprocessStatus;
  relative_path: string;
}

export interface ProposalResponse {
  action: 'affirmative' | 'negative' | 'pending';
  response: string;
  data_files: DataFileEntity[];
  answers: ProposalAnswerEntity[];
  submitted_by?: ProposalEntitySubmittedBy;
  hide_identity: boolean;
}

export interface ProposalEntity {
  id: number;
  search: number | SearchEntity;
  data_provider_response?: ProposalResponse | null;
  data_buyer_response?: {
    action: 'affirmative' | 'negative' | 'pending';
    response: string;
    submitted_by?: ProposalEntitySubmittedBy;
    hide_identity: boolean;
  } | null;
  data_buyer: OrganizationDetails;
  data_buyer_child: OrganizationDetails;
  data_buyer_master: OrganizationDetails;
  data_provider: Partial<DataProviderEntity>;
  third_party_response: boolean;
  archived: boolean;
  assigned_to?: {
    first_name: string;
    last_name: string;
    username: string;
  } | null;
  conversation_id?: number;
  nomad_guidance?: string;
  created_at: string;
}

export interface OrganizationDetails {
  email?: string;
  short_description?: string;
  long_description?: string;
  vague_description?: string;
  classification?: string;
  name?: string;
  avatar?: { id: number; image: CloudinaryResponse };
  display_master_org_details?: boolean;
}

export interface FilterEntityOption {
  id: number | string;
  name: string;
}

export enum SEARCH_FILTER_KEYS {
  exclude_providers = 'exclude_providers',
  exclude_providers_drm = 'exclude_providers_drm',
  data_lag = 'data_lag',
  price = 'price',
  history = 'history',
  granularity = 'granularity',
  bulk_download = 'bulk_download',
  sector = 'sector',
  delivery_format = 'delivery_format',
}

export interface SearchFilterEntity {
  created_at: string;
  description: string;
  id: number;
  key: keyof typeof SEARCH_FILTER_KEYS;
  name: string;
  options: FilterEntityOption[];
  type: 'boolean' | 'numeric' | 'category' | 'timediff';
  updated_at: string;
}

export interface InvitedUserEntity {
  id: number;
  email: string;
  organization?: number;
  data_provider?: number;
  created_at: string;
  udpated_at: string;
}

export interface MatchedOrganizationEntity {
  classification: string;
  long_description: string;
  name: string;
  short_description: string;
  site_name: string;
  url: string;
  vague_description: string;
}
export interface ProposalRejectionOption {
  id: number;
  text: string;
  visible: boolean;
  ordinal: number;
  role: ProposalRejectionOptionRole;
}

export enum ProposalRejectionOptionRole {
  data_buyer = 'data_buyer',
  data_provider = 'data_provider',
}

export type Role = 'dataProvider' | 'dataBuyer' | 'nomadAdmin' | '';
export type RoleStr = 'Data Provider' | 'Data Buyer' | 'Nomad Admin' | '';

export type NotificationType = 'ACCEPTED' | 'REJECTED' | 'ARCHIVED';
export interface NotificationEntity {
  id: number;
  type: NotificationType;
  proposal_id: number;
  data_provider: number;
  created_at: string;
}

export interface DjangoListResponse<Entity> {
  results: Entity[];
  count: number;
  next: string | null;
  prev: string | null;
}

export interface TalkjsConversationEntity {
  id: string;
  subject?: string;
  topicId?: string;
  photoUrl?: string;
  welcomeMessages?: string[];
  custom?: {
    proposal?: string;
    search: string;
    chat_context?: string;
    environment_name?: string;
    reviewer_id?: number;
  };
  participants?: {
    'user-data-buyer': { access: 'Read' | 'ReadWrite'; notify: boolean };
    'user-data-provider': { access: 'Read' | 'ReadWrite'; notify: boolean };
  };
}

export type UserStatusType = 'Admin' | 'Member' | 'Disabled';
export type UserStatusActionType = 'Admin' | 'Member' | 'Disable';

export interface TeamMutationObject {
  id: number;
  admin?: boolean;
  active?: boolean;
}

export interface InstructionEntity {
  id: number;
  key: string;
  value: string[];
}

export interface Permission {
  id: number | null;
  permission_name: string;
  has_access: boolean;
  unlimited: boolean;
  permission_quantity: number;
  quantity_remaining: number;
  subscription_id: string | null;
  subscription_active: boolean;
  subscription_chargebee_id: string | null;
  plan_id: string | null;
  plan_family: string | null;
}

export interface PlanPermissionData {
  id: number;
  plan: {
    id: number;
    name: string;
  };
  permission: Permission;
}

export type PlanPermission = Record<string, PlanPermissionData[]>;

export interface ReadOnlyUser
  extends Pick<
    User,
    | 'id'
    | 'username'
    | 'first_name'
    | 'last_name'
    | 'email'
    | 'admin'
    | 'active'
  > {
  avatar_url: string;
  organization_name?: string;
}

export interface RetrievedCompanyInfo {
  name: string;
  description: string;
  location: {
    name: string;
    locality: string;
    region: string;
    metro: string;
    country: string;
    continent: string;
    street_address: string;
    address_line_2: string | null;
    postal_code: string;
    geo: string;
  };
  industry: string;
  display_name: string;
  website: string;
  avatar_url: string;
}

export interface DataVendor extends ACLEntity {
  id: number;
  master_organization: number;
  name: string;
  domain: string;
  description: string;
  classification: string;
  avatar_url: string;
  data_provider: number | null;
  owner?: number;
  last_interaction_date: string;
  is_primary?: boolean;
  am_a_customer: boolean;
}

export type EmailAssociation = {
  id?: number;
  email: string;
  is_primary: boolean;
};

export type ContactPrimaryAssociation = {
  email?: EmailAssociation;
  data_vendor?: DataVendor;
};

export interface Contact extends ACLEntity {
  id: number;
  first_name: string;
  last_name: string;
  title: string;
  internal_designation: boolean;
  contact_owner: number;
  address: string;
  phone_number: string;
  linkedin: string;
  primary_associations: ContactPrimaryAssociation;
  email_associations?: EmailAssociation[];
  avatar_url?: string;
  description: string;
  company_name?: string;
  name?: string;
  is_primary?: boolean;
}

export type KeyCount = { [key: string]: number };

export interface SearchStateProps {
  user: User;
  unreadMessages: KeyCount;
}

export interface FetchSearchesParams {
  active?: boolean;
  all_searches?: boolean;
  review_status?: 'approved' | 'needs_editting' | 'under_review' | 'denied';
  review_status__ne?: 'approved' | 'needs_editting' | 'under_review' | 'denied';
  search_text?: string;
  depth?: 'individual' | 'organization' | 'master-organization';
  page?: number;
  ordering?: string;
  size?: number;
  data_provider?: number | null;
  data_provider_response?: string;
  review_actioned?: boolean;
  needs_review?: boolean;
}

export interface ACLEntity {
  is_editable: boolean;
  is_deletable: boolean;
}

export interface ActivityAssociation {
  id?: number;
  name: string;
  object_id: number;
  table_name: string;
  is_mandatory?: boolean;
  selected?: boolean;
  editable?: boolean;
  is_viewable?: boolean;
}

export interface DataProduct extends ACLEntity {
  id: number;
  name: string;
  description: string;
  access_details: string;
  currently_purchased: boolean;
  owner: number;
  purchases: DataProductPurchase[];
  costs: DataProductCost[];
  restrictions: DataProductRestriction[];
  is_primary?: boolean;
  data_vendor?: DataVendor;
}

export type TimeScaleType =
  | 'perpetual_license'
  | 'per_year'
  | 'per_quarter'
  | 'per_month'
  | 'consumption_based';

export interface DataProductPurchase {
  id?: number;
  cost?: number;
  currency?: string;
  note?: string;
  purchase_date?: string;
  renewal_date?: string;
  opt_out_date?: string | null;
  start?: string;
  end?: string;
  timescale?: TimeScaleType;
}

export interface ContactFormFields extends Partial<Contact> {
  contact: number;
}

export interface ContactFormProps {
  setValue?: UseFormSetValue<ContactFormFields>;
  getValues?: UseFormGetValues<ContactFormFields>;
  contact?: Contact;
  contactOwner?: User;
  register?: UseFormRegister<ContactFormFields>;
  isValid?: boolean;
  fieldsRef?: Partial<Record<string, Field>>;
}

export type WithInfiniteQuery<T> = {
  data: InfiniteData<T>;
  infiniteQueryFetch: {
    isFetching: boolean;
    hasNextPage: boolean | undefined;
    fetchNextPage: (
      options?: FetchNextPageOptions | undefined
    ) => Promise<InfiniteQueryObserverResult>;
  };
};

export interface DataProductCost {
  id?: number;
  amount?: number;
  currency?: string;
  time_period?: TimeScaleType;
}
export interface SearchQueryResponse {
  results: SearchEntity[];
  count: number;
  next: string | null;
  previous: string | null;
}

export interface GenericAssociation {
  left_object_id?: number;
  left_content_type?: string;
  right_object_id?: number;
  right_content_type?: string;
  is_primary?: boolean;
  name?: string;
}

export interface DataProductRestriction {
  id: number;
  title: string;
  restriction: string;
}

export interface DataProductRestrictionsResponse {
  results: DataProductRestriction[];
  count: number;
}

export enum AIConversationStatus {
  CREATED = 'created',
  QUEUED = 'queued',
  IN_PROCESS = 'in_process',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum AIConversationSearchType {
  DRM = 'drm',
  DOCUMENT = 'document',
}

export interface AIConversation {
  id: number;
  name: string;
  user: number;
  data_vendors: DataVendor[];
  all_companies: boolean;
  files: File[];
  all_files: boolean;
  last_submission: string;
  last_completion: string;
  updated_at: string;
  status: AIConversationStatus;
  file_names?: string[];
  data_vendor_names?: string[];
  search_type: AIConversationSearchType;
  custom_chat?: number;
}

export interface AIConversationDetail
  extends Omit<AIConversation, 'custom_chat'> {
  custom_chat?: CustomAIChat;
}

export enum MessageRole {
  SYSTEM = 'system',
  USER = 'user',
  ASSISTANT = 'assistant',
  FUNCTION = 'function',
}

export interface AIConversationMessage {
  id: number;
  ai_conversation: number;
  text: string;
  role: MessageRole;
  file: string | null;
}

export type FieldType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'currency'
  | 'rich_text'
  | 'long_text'
  | 'date'
  | 'list_of_strings'
  | 'multiple_choice';

export enum DrmPage {
  DATA_VENDOR = 'data_vendor',
  CONTACT = 'contact',
  DATASET = 'dataset',
}

export interface LinkEntityType {
  id: number;
  entity: DrmEntityBackend;
}

export interface LinkEntityInfoType extends LinkEntityType {
  avatar?: string;
  name?: string;
  description?: string;
}

export enum DrmEntityBackend {
  DATA_VENDOR = 'data_vendor',
  CONTACT = 'contact',
  DATASET = 'data_product',
}

export interface FieldTabPlacement {
  field_name: string;
  field_type: FieldType;
  label: string;
}

export interface CustomTab {
  id: number;
  name: string;
  icon: string;
  order: number;
  drm_page: DrmPage;
  field_placements: FieldTabPlacement[];
}

export type DataVendorDetailsTabType = {
  eventKey: 'details' | 'contacts' | 'activity' | 'files';
  title: string;
};

export type DataProductTabType = {
  eventKey: 'activity' | 'details' | 'restrictions' | 'associations' | 'files';
  title: string;
};

export type ContactTabType = {
  eventKey: 'activity' | 'details' | 'associations';
  title: string;
};

export type UserDetailsTabType = {
  eventKey: 'licenses' | 'permissions';
  title: string;
};

export type ReviewTabType = {
  eventKey: ReviewTabEnum;
  title: string;
};

export enum ReviewTabEnum {
  actioned = 'actioned',
  needs_review = 'needs_review',
}

export type GroupEntityLabelKey =
  | 'DataVendor'
  | 'Contact'
  | 'DataProduct'
  | 'Searches'
  | 'ALL';

export function useGroupEntityLabel() {
  const { t } = useTranslation();
  const groupEntityLabel: Record<GroupEntityLabelKey, string> = {
    ALL: 'Associations',
    DataVendor: t(TranslationKeys.company_other, {
      postProcess: PostProcessKeys.TitleCase,
    }),
    Contact: t(TranslationKeys.contact_other, {
      postProcess: PostProcessKeys.TitleCase,
    }),
    DataProduct: t(TranslationKeys.dataProduct_other, {
      postProcess: PostProcessKeys.TitleCase,
    }),
    Searches: 'Requests',
  };

  return groupEntityLabel;
}

export interface CheckRequestDescriptionContent {
  result: 'good' | 'bad';
  feedback: string;
}

export interface CheckRequestDescription {
  prompt_history_id: number;
  content: CheckRequestDescriptionContent;
}

export enum CheckDescriptionStatus {
  default = 'default',
  good = 'good',
  bad = 'bad',
  error = 'error',
}

export interface ProviderProposalResponseFields {
  response: string;
  answers: Array<{ id: number; question: string; response: string }>;
  check_seller_response_status?: CheckDescriptionStatus;
  check_seller_response_feedback?: string;
  hide_identity: boolean;
  data_files: Array<DataFileEntity>;
  prompt_history_id?: number;
}

export type ChargebeeSubscription = {
  id: number;
  owner: number;
  users: number[];
  customer_id: string;
  subscription_id: string;
  active: boolean;
  plan_id: string;
  plan_fk: {
    id: number;
    name: string;
  };
  plan_quantity: number;
  created_at: string;
  updated_at: string;
  started_at: string;
  activated_at: string;
  remaining: number;
};

export interface EnrichedSubscription extends ChargebeeSubscription {
  permissions: PlanPermissionData[];
  user_id?: number;
  selected: boolean;
}

export type RootState = {
  user: {
    user: User;
  };
  permissions: {
    permissions: Record<string, Permission>;
    loaded: boolean;
  };
  searches: {
    counts: {
      my_active_searches_count: number;
    };
    loaded: boolean;
  };
  chat: {
    unreadMessages: { [key: number]: string };
  };
};

export interface LocationStateType {
  counterToGoBack: number;
  backLinkTitle?: string;
}

export interface InfiniteScrollProps {
  fetchNextPage: () => Promise<any>;
  hasNextPage?: boolean;
}

export enum ACLAction {
  VIEW = 'view',
  EDIT = 'edit',
  DELETE = 'delete',
}

export enum ACLAccessLevel {
  EVERYTHING = 'everything',
  OWN = 'own',
  ORGANIZATION = 'organization',
  NONE = 'none',
}

export const ACLAccessLevelValues = {
  [ACLAccessLevel.EVERYTHING]: 'Everything',
  [ACLAccessLevel.OWN]: 'Owned Only',
  [ACLAccessLevel.ORGANIZATION]: 'Team Only',
  [ACLAccessLevel.NONE]: 'None',
};

export enum ACLContentType {
  contact = 'contact',
  dataproduct = 'dataproduct',
  datavendor = 'datavendor',
  activity = 'activity',
}

export const ACLContentTypeValues = {
  [ACLContentType.contact]: 'Contacts',
  [ACLContentType.dataproduct]: 'Datasets',
  [ACLContentType.datavendor]: 'Companies',
  [ACLContentType.activity]: 'Activities',
};

export interface ContentType {
  id: number;
  model: string;
  app_label: string;
}

export interface ACLUSer {
  id: number;
  strapi_user: number;
  access_level: ACLAccessLevel;
  action: ACLAction;
  content_type: ContentType;
}

export interface MasterOrganization {
  id: number;
  name: string;
  unqualified: boolean;
  classification?: string;
  short_description?: string;
  long_description?: string;
  vague_description?: string;
  max_allowed_bulk_uploaded_files?: number;
  created_at?: string;
  updated_at?: string;
}

export type Organization = {
  id: number;
  master_organization: number;
  name: string;
  classification: string;
  short_description: string;
  long_description: string;
  email: string;
  address: string;
  vague_description: string;
  created_at: string;
  updated_at: string;
  display_master_org_details: boolean;
  avatar: { id: number; image: CloudinaryResponse };
};

export enum IamACustomerDrmEntity {
  DATA_VENDOR = 'data_vendor',
  DATASET = 'data_product',
}

export interface IamACustomerDRMEntityType {
  id: number;
  type: IamACustomerDrmEntity;
  iAmACustomer: boolean;
}

export interface MultiPartUploadResponse {
  id: number;
  file: string;
  file_name: string;
  size: number;
  owner: User;
  upload_status: string;
  multipart_data: MultipartData;
}

export interface MultipartData {
  upload_id: string;
  presigned_urls: PresignedURL[];
}

export interface PresignedURL {
  part: number;
  presigned_url: string;
}

export type FetchFilters = Record<string, string>;

export type CustomUseQueryProps<T> = {
  filters: FetchFilters;
  options?: Omit<UseQueryOptions<T, unknown, T, any>, 'queryKey' | 'queryFn'>;
};

export interface FeaturedDataProvider {
  id: number;
  use_case: string;
  data_category: string;
  created_at: string;
  updated_at: string;
  provider_status: FeaturedDataProviderStatus;
}

export enum FeaturedDataProviderStatus {
  already_in_drm = 'already_in_drm',
  data_requested = 'data_requested',
  submitted_by_other = 'submitted_by_other',
  request_data = 'request_data',
}

export interface CustomAIChat {
  id: number;
  name: string;
}

/**
 * Extended dropzone file definition to inject error messages in the file objects.
 */
export type ExtendedDropzoneFile = DropzoneFile & {
  error?: string;
};

export interface BulkFilesConfirmation {
  primary_object_type?: string;
  data_vendor_pk?: number | null;
  data_product_pk?: number | null;
  type: FileType;

  /**
   * Dropzone file IDs.
   */
  files: string[];
}

export type EmailVerified = {
  email_verified: boolean;
};

export interface AnonymousDataProvider {
  anonymous_description: string;
  classification: string;
}

export interface ArtificialProposalResponse {
  id: number;
  search: number;
  data_provider: AnonymousDataProvider;
  proposal: number | null;
  response: string;
  created_at: string;
}

export enum TableName {
  Searches = 'Searches',
  Contact = 'Contact',
  DataVendor = 'DataVendor',
  DataProduct = 'DataProduct',
}

export interface SearchConversationsCount {
  conversations_count: number;
}

export interface SiteSetting {
  sender_email: string;
  minimum_price_warning_for_maximum_price_filter: number;
}
